import type { EquityGrant, VestEvent } from "../api-modules/types.js";

/**
 * Web-side mirror of `shared/src/util/equity.ts` — vesting helpers used
 * by the `/equity-grants` page. Web does NOT import from `@hearth/shared`
 * (locked since 5.1; pulls `node:crypto` into the Vite bundle), so the
 * core math is duplicated. Behavior must stay in sync with the shared
 * implementation; the matching shared/test/equity.test.ts cases are
 * the contract.
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
function pad4(n: number): string {
  return n.toString().padStart(4, "0");
}

export function addMonthsClamped(isoDate: string, months: number): string {
  if (!ISO_DATE_RE.test(isoDate)) {
    throw new Error(`addMonthsClamped: invalid date "${isoDate}"`);
  }
  const [y, m, d] = isoDate.split("-").map(Number) as [number, number, number];
  const total0 = y * 12 + (m - 1) + months;
  const newY = Math.floor(total0 / 12);
  const newM0 = total0 % 12;
  const lastDay = new Date(Date.UTC(newY, newM0 + 1, 0)).getUTCDate();
  const newD = Math.min(d, lastDay);
  return `${pad4(newY)}-${pad2(newM0 + 1)}-${pad2(newD)}`;
}

export interface GenerateVestEventsArgs {
  readonly grantDate: string;
  readonly totalShares: number;
  readonly cliffMonths: number;
  readonly vestMonths: number;
  readonly periodMonths: number;
  readonly cliffPctOfTotal?: number;
}

export function generateVestEvents(
  args: GenerateVestEventsArgs,
): VestEvent[] {
  const { grantDate, totalShares, cliffMonths, vestMonths, periodMonths } =
    args;
  if (totalShares < 0)
    throw new Error("generateVestEvents: totalShares must be >= 0");
  if (cliffMonths < 0 || vestMonths <= 0 || periodMonths <= 0)
    throw new Error(
      "generateVestEvents: cliffMonths >= 0, vestMonths > 0, periodMonths > 0",
    );
  if (vestMonths < cliffMonths)
    throw new Error("generateVestEvents: vestMonths must be >= cliffMonths");
  const postCliffMonths = vestMonths - cliffMonths;
  if (postCliffMonths % periodMonths !== 0) {
    throw new Error(
      "generateVestEvents: (vestMonths - cliffMonths) must be a multiple of periodMonths",
    );
  }
  const postCliffPeriods = postCliffMonths / periodMonths;

  const events: VestEvent[] = [];
  const hasCliff = cliffMonths > 0;
  const cliffPct = args.cliffPctOfTotal ?? cliffMonths / vestMonths;
  const cliffShares = hasCliff ? totalShares * cliffPct : 0;
  const remaining = totalShares - cliffShares;
  const perPeriodShares =
    postCliffPeriods === 0 ? 0 : remaining / postCliffPeriods;

  if (hasCliff) {
    events.push({
      date: addMonthsClamped(grantDate, cliffMonths),
      shares: cliffShares,
      is_cliff: true,
    });
  }
  for (let i = 1; i <= postCliffPeriods; i++) {
    const monthsFromGrant = cliffMonths + i * periodMonths;
    const isLast = i === postCliffPeriods;
    const shares = isLast
      ? totalShares - cliffShares - perPeriodShares * (postCliffPeriods - 1)
      : perPeriodShares;
    events.push({
      date: addMonthsClamped(grantDate, monthsFromGrant),
      shares,
    });
  }
  return events;
}

/** Sum of shares vesting per YYYY-MM in [from, to] across grants. */
export function vestingCalendar(
  grants: ReadonlyArray<Pick<EquityGrant, "vest_events">>,
  from: string,
  to: string,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const g of grants) {
    for (const v of g.vest_events ?? []) {
      if (v.date < from || v.date > to) continue;
      const ym = v.date.slice(0, 7);
      out.set(ym, (out.get(ym) ?? 0) + v.shares);
    }
  }
  return out;
}

export interface VestTaxProjection {
  readonly grant_id: string;
  readonly ticker: string;
  readonly grant_type: EquityGrant["grant_type"];
  readonly shares_vesting: number;
  readonly projected_ordinary_income_cents: number | null;
  readonly projected_bargain_element_cents: number | null;
}

/** Back-of-envelope tax projection per grant. NOT authoritative. */
export function projectVestTaxEvents(
  grants: ReadonlyArray<EquityGrant>,
  from: string,
  to: string,
): VestTaxProjection[] {
  const out: VestTaxProjection[] = [];
  for (const g of grants) {
    let shares = 0;
    for (const v of g.vest_events ?? []) {
      if (v.date < from || v.date > to) continue;
      shares += v.shares;
    }
    if (shares <= 0) continue;
    const fmv = g.most_recent_fmv_cents ?? null;
    let ordinary: number | null = null;
    let bargain: number | null = null;
    switch (g.grant_type) {
      case "RSU":
      case "RSA":
        ordinary = fmv !== null ? Math.round(fmv * shares) : null;
        break;
      case "ESPP":
        ordinary = fmv !== null ? Math.round(fmv * shares * 0.15) : null;
        break;
      case "ISO":
      case "NSO": {
        const strike = g.strike_price_cents ?? null;
        if (fmv !== null && strike !== null) {
          bargain = Math.round((fmv - strike) * shares);
        }
        break;
      }
    }
    out.push({
      grant_id: g.grant_id,
      ticker: g.ticker,
      grant_type: g.grant_type,
      shares_vesting: shares,
      projected_ordinary_income_cents: ordinary,
      projected_bargain_element_cents: bargain,
    });
  }
  return out;
}
